import { importCodes, importPollingInterval } from './onboarding';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Alert, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { catalogAdminKey } from './queries';

export interface ImportJob {
  job_id: string;
  connector_account_id: string;
  access: string;
  content_kind: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
  pages_processed: number;
  has_checkpoint: boolean;
  last_error_code: string | null;
  criteria: Record<string, unknown>;
}
export function ImportJobs() {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const [account, setAccount] = useState(''),
    [access, setAccess] = useState(''),
    [kind, setKind] = useState('supplier_hotel_list');
  const [countries, setCountries] = useState(''),
    [destinations, setDestinations] = useState(''),
    [hotels, setHotels] = useState('');
  const [confirmed, setConfirmed] = useState(false),
    [jobId, setJobId] = useState(''),
    [followingSince, setFollowingSince] = useState(0),
    [offset, setOffset] = useState(0);
  const accounts = useQuery(
    [...catalogAdminKey, 'import-accounts'],
    async () =>
      (
        await client.get<{
          data: Array<{
            connector_account_id: string;
            display_name: string;
            status: string;
            access_mode: string;
            access_codes: string[];
          }>;
        }>('hotel-catalog-admin/import-accounts')
      ).data.data,
    { retry: false },
  );
  const page = useQuery(
    [...catalogAdminKey, 'import-jobs', offset],
    async () =>
      (
        await client.get<{ data: ImportJob[]; meta: { count: number } }>('hotel-catalog-admin/import-jobs', {
          params: { limit: 25, offset },
        })
      ).data,
    { retry: false },
  );
  const job = useQuery(
    [...catalogAdminKey, 'import-job', jobId],
    async () =>
      (await client.get<{ data: ImportJob }>(`hotel-catalog-admin/import-jobs/${encodeURIComponent(jobId)}`)).data.data,
    {
      enabled: !!jobId,
      retry: false,
      refetchInterval: (data) => importPollingInterval(data?.status, followingSince),
      refetchIntervalInBackground: false,
    },
  );
  const chosen = accounts.data?.find((a) => a.connector_account_id === account);
  const follow = (id: string) => {
    setJobId(id);
    setFollowingSince(Date.now());
  };
  const save = useMutation(
    async () => {
      const criteria = {
        countries: importCodes(countries),
        destination_codes: importCodes(destinations),
        hotel_codes: importCodes(hotels),
        category_codes: [],
        group: null,
      };
      return (
        await client.post<{ data: ImportJob }>('hotel-catalog-admin/import-jobs', {
          connector_account_id: account,
          access,
          content_kind: kind,
          criteria,
          max_size: 100,
          confirm_scope: confirmed,
        })
      ).data.data;
    },
    {
      onSuccess: (data) => {
        follow(data.job_id);
        setConfirmed(false);
        cache.invalidateQueries([...catalogAdminKey, 'import-jobs']);
      },
    },
  );
  const change = () => {
    setConfirmed(false);
    save.reset();
  };
  const hotelFeed = ['hotel_master', 'supplier_hotel_list'].includes(kind);
  const bounded =
    !hotelFeed || importCodes(countries).length + importCodes(destinations).length + importCodes(hotels).length > 0;
  return (
    <Stack spacing={2} sx={{ my: 3 }}>
      <Typography variant="h5">{t('hotelOnboarding.imports')}</Typography>
      <Alert severity="info">{t('hotelOnboarding.importNotice')}</Alert>
      {accounts.isError && <Alert severity="error">{t('hotelOnboarding.loadError')}</Alert>}
      <TextField
        select
        label={t('hotelOnboarding.account')}
        value={account}
        disabled={save.isLoading}
        onChange={(e) => {
          setAccount(e.target.value);
          setAccess('');
          change();
        }}
      >
        {(accounts.data ?? [])
          .filter((a) => a.status === 'active' && a.access_mode === 'include')
          .map((a) => (
            <MenuItem key={a.connector_account_id} value={a.connector_account_id}>
              {a.display_name} · {a.connector_account_id}
            </MenuItem>
          ))}
      </TextField>
      <TextField
        select
        label={t('hotelOnboarding.access')}
        value={access}
        disabled={save.isLoading}
        onChange={(e) => {
          setAccess(e.target.value);
          change();
        }}
      >
        {(chosen?.access_codes ?? []).map((a) => (
          <MenuItem key={a} value={a}>
            {a}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label={t('hotelOnboarding.feed')}
        value={kind}
        disabled={save.isLoading}
        onChange={(e) => {
          setKind(e.target.value);
          setCountries('');
          setDestinations('');
          setHotels('');
          change();
        }}
      >
        {['supplier_hotel_list', 'hotel_master', 'destination_list', 'board_list', 'category_list'].map((k) => (
          <MenuItem key={k} value={k}>
            {t(`hotelOnboarding.${k}`)}
          </MenuItem>
        ))}
      </TextField>
      {hotelFeed && (
        <TextField
          label={t('hotelOnboarding.countries')}
          value={countries}
          disabled={save.isLoading}
          inputProps={{ maxLength: 100 }}
          onChange={(e) => {
            setCountries(e.target.value.toUpperCase());
            change();
          }}
        />
      )}
      {(hotelFeed || kind === 'destination_list') && (
        <TextField
          label={t('hotelOnboarding.destinationCodes')}
          value={destinations}
          disabled={save.isLoading}
          multiline
          inputProps={{ maxLength: 20000 }}
          onChange={(e) => {
            setDestinations(e.target.value);
            change();
          }}
        />
      )}
      {hotelFeed && (
        <TextField
          label={t('hotelOnboarding.hotelCodes')}
          value={hotels}
          disabled={save.isLoading}
          multiline
          inputProps={{ maxLength: 40000 }}
          onChange={(e) => {
            setHotels(e.target.value);
            change();
          }}
        />
      )}
      <Typography>
        {t('hotelOnboarding.scope')}: {account} / {access} · {kind} · {countries || '—'} / {destinations || '—'} /{' '}
        {hotels || '—'}
      </Typography>
      <FormControlLabel
        control={<Checkbox checked={confirmed} disabled={save.isLoading} onChange={(_, v) => setConfirmed(v)} />}
        label={t('hotelOnboarding.confirmScope')}
      />
      <Button
        disabled={!confirmed || !account || !access || !bounded || save.isLoading || accounts.isError}
        onClick={() => save.mutate()}
      >
        {t('hotelOnboarding.enqueue')}
      </Button>
      {save.isError && <Alert severity="error">{t('hotelOnboarding.enqueueError')}</Alert>}
      {job.data && (
        <Alert severity={job.data.status === 'terminal_failed' ? 'warning' : 'info'}>
          {job.data.job_id} · {job.data.status} · {t('hotelOnboarding.pages')}: {job.data.pages_processed} ·{' '}
          {t('hotelOnboarding.attempts')}: {job.data.attempt_count}/{job.data.max_attempts}{' '}
          {job.data.has_checkpoint && t('hotelOnboarding.checkpoint')} {job.data.last_error_code}
        </Alert>
      )}
      {job.isError && <Alert severity="error">{t('hotelOnboarding.loadError')}</Alert>}
      {!!jobId && (
        <Button
          onClick={() => {
            setFollowingSince(Date.now());
            job.refetch();
          }}
        >
          {t('hotelOnboarding.refreshStatus')}
        </Button>
      )}
      <Alert severity="info">{t('hotelOnboarding.resumeNotice')}</Alert>
      <Typography variant="h6">{t('hotelOnboarding.existingJobs')}</Typography>
      {page.isError && <Alert severity="error">{t('hotelOnboarding.loadError')}</Alert>}
      {(page.data?.data ?? []).map((j) => (
        <Button key={j.job_id} onClick={() => follow(j.job_id)}>
          {j.job_id} · {j.content_kind} · {j.access} · {j.status}
        </Button>
      ))}
      <Stack direction="row">
        <Button disabled={!offset || page.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 25))}>
          {t('hotelCatalogAdmin.previous')}
        </Button>
        <Button
          disabled={page.isFetching || (page.data?.meta.count ?? 0) < 25 || offset >= 10000}
          onClick={() => setOffset((v) => v + 25)}
        >
          {t('hotelCatalogAdmin.next')}
        </Button>
        <Button onClick={() => page.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>
      </Stack>
    </Stack>
  );
}
